import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Design } from './types/editor.types';
import {
  listTemplates,
  getTemplateDetail,
  fetchDesignJson,
  saveTemplateFlow,
  updateTemplateFlow,
  moveToTrash,
  deletePermanently,
  type ITemplateMeta,
  type ITemplateDetail,
} from './api';
import { templateKeys as trashTemplateKeys } from 'core/features/templates/hooks';
import { permanentlyDeleteTemplate } from 'core/features/templates/api';

// ─── Query Key Factory ───────────────────────────────────────────────────────

export const templateKeys = {
  root: ['content-builder-templates'] as const,
  list: () => [...templateKeys.root, 'list'] as const,
  detail: (id: number | string) => [...templateKeys.root, 'detail', String(id)] as const,
} as const;

// ─── List Templates ──────────────────────────────────────────────────────────

export function useListTemplates() {
  return useQuery({
    queryKey: templateKeys.list(),
    queryFn: listTemplates,
    staleTime: 2 * 60 * 1000,
    // Normalize dict { "0": {...}, "1": {...} } → array
    select: (res) => Object.values(res.All_templates ?? {}) as ITemplateMeta[],
  });
}

// ─── Get Single Template + Design JSON ──────────────────────────────────────

export function useTemplate(id: number | string | null | undefined) {
  return useQuery({
    queryKey: templateKeys.detail(id!),
    queryFn: async () => {
      const detail: ITemplateDetail = await getTemplateDetail(id!);
      // Fetch the design JSON from the presigned URL (no auth)
      const design: Design | null = await fetchDesignJson(detail?.data);
      return { detail, design };
    },
    enabled: id != null,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── Save New Template (full flow) ───────────────────────────────────────────

export function useSaveTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      name: string;
      design: Design;
      tagName?: string;
      description?: string;
    }) => saveTemplateFlow(params),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.list() });
    },
  });
}

// ─── Update Existing Template (full flow) ────────────────────────────────────

export function useUpdateTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (params: {
      id: number | string;
      name: string;
      design: Design | null;
      tagName?: string;
    }) => updateTemplateFlow(params),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: templateKeys.list() });
      qc.invalidateQueries({ queryKey: templateKeys.detail(id) });
    },
  });
}

// ─── Rename Template ─────────────────────────────────────────────────────────
// Per API spec, rename requires re-uploading the same design with a new name.

export function useRenameTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, newName }: { id: number | string; newName: string }) => {
      // Fetch the current design from the presigned URL first
      const detail = await getTemplateDetail(id);
      const design = await fetchDesignJson(detail.data);
      return updateTemplateFlow({ id, name: newName, design });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.list() });
    },
  });
}

// ─── Move to Trash ───────────────────────────────────────────────────────────

export function useMoveTemplateToTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => moveToTrash(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.list() });
    },
  });
}

// ─── Permanently Delete ──────────────────────────────────────────────────────

export function useDeleteTemplatePermanently() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => deletePermanently(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.list() });
      // Also refresh the trash page list
      qc.invalidateQueries({ queryKey: trashTemplateKeys.trashList() });
    },
  });
}

export function useDeleteTemplateFromTrash() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number | string) => permanentlyDeleteTemplate(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: templateKeys.list() });
      // Also refresh the trash page list
      qc.invalidateQueries({ queryKey: trashTemplateKeys.trashList() });
    },
  });
}
